<template>
  <div v-if="visible" class="ai-thinking-overlay" id="ai-thinking-overlay">
    <div class="ai-thinking-content">
      <!-- 动态大脑图标 -->
      <div class="ai-brain">
        <div class="brain-ring"></div>
        <div class="brain-ring"></div>
        <div class="brain-ring"></div>
        <div class="brain-core">
          <div class="brain-icon">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C9.5 2 7.5 3.5 7.5 6C7.5 7.5 8.2 8.8 9.3 9.6C9.1 10.1 9 10.6 9 11.2C9 12.8 10.3 14.1 12 14.1C13.7 14.1 15 12.8 15 11.2C15 10.6 14.9 10.1 14.7 9.6C15.8 8.8 16.5 7.5 16.5 6C16.5 3.5 14.5 2 12 2Z" fill="currentColor" opacity="0.9"/>
              <path d="M12 15C9.5 15 7.5 16.5 7.5 19C7.5 20.5 8.7 21.7 10.2 21.9L10.4 21.9L10.6 21.9C11.1 21.9 11.5 21.5 11.5 21C11.5 20.5 11.1 20.1 10.6 20.1C10.5 20.1 10.4 20.1 10.3 20.1C9.7 19.9 9.3 19.4 9.3 18.8C9.3 17.5 10.4 16.5 11.8 16.5H12.2C13.6 16.5 14.7 17.5 14.7 18.8C14.7 19.4 14.3 19.9 13.7 20.1C13.6 20.1 13.5 20.1 13.4 20.1C12.9 20.1 12.5 20.5 12.5 21C12.5 21.5 12.9 21.9 13.4 21.9L13.6 21.9L13.8 21.9C15.3 21.7 16.5 20.5 16.5 19C16.5 16.5 14.5 15 12 15Z" fill="currentColor"/>
              <circle cx="10.5" cy="7.5" r="1.5" fill="#fff" opacity="0.6"/>
              <circle cx="13.5" cy="7.5" r="1.5" fill="#fff" opacity="0.6"/>
            </svg>
          </div>
        </div>
      </div>

      <!-- 标题 -->
      <div class="ai-thinking-text">
        <div class="thinking-title">
          <span class="title-icon">✨</span>
          AI 智能分析中
          <span class="title-icon">✨</span>
        </div>

        <!-- 步骤列表 -->
        <div class="thinking-steps" ref="stepsContainerRef">
          <transition-group name="step-fade">
            <div v-for="(step, index) in steps" :key="step.id"
                 class="thinking-step"
                 :class="{
                   active: currentStepIndex >= index,
                   completed: currentStepIndex > index
                 }"
                 :ref="el => stepRefs[index] = el">
              <div class="step-indicator">
                <span v-if="currentStepIndex > index" class="step-check">✓</span>
                <span v-else-if="currentStepIndex === index" class="step-loading"></span>
                <span v-else class="step-dot">•</span>
              </div>
              <span class="step-text">{{ step.text }}</span>
            </div>
          </transition-group>
        </div>

        <!-- 进度条 -->
        <div class="ai-progress">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
          </div>
          <div class="progress-text">{{ progressPercent }}%</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, nextTick, watch } from 'vue';
import { aiAnimationService } from '@/utils/aiAnimationService';

interface ThinkingStep {
  id: number;
  text: string;
}

interface AIThinkingOverlayProps {
  steps?: ThinkingStep[];
  stepDelay?: number;
  onComplete?: () => void;
  loadStatus?: boolean;
  apiCompleted?: boolean;
  detailDataLoaded?: boolean; // 详情页数据是否已加载完成
  startStepIndex?: number; // 从第几步开始（0 = 第 1 步，8 = 第 9 步）
}

const props = withDefaults(defineProps<AIThinkingOverlayProps>(), {
  stepDelay: 800,
  startStepIndex: 0, // 默认从第 1 步开始
  steps: () => [
    { id: 1, text: '正在读取实控人征信数据...' },
    { id: 2, text: '正在同步企业工商基础信息...' },
    { id: 3, text: '正在拉取亚马逊店铺经营数据...' },
    { id: 4, text: '正在分析近 12 个月销售及回款数据...' },
    { id: 5, text: '正在计算店铺资产与库存价值...' },
    { id: 6, text: '正在拆解商品品类与定价结构...' },
    { id: 7, text: '正在排查企业合规风险点...' },
    { id: 8, text: '正在分析企业刚性负债情况...' },
    { id: 9, text: '正在生成风控终审评级数据...' },
    { id: 10, text: '正在汇总尽调数据模块...' },
    { id: 11, text: '正在生成报告可视化图表...' },
    { id: 12, text: '正在排版最终尽调报告内容...' }
  ]
});

const emit = defineEmits<{
  complete: [];
  step9Complete: [];
}>();

const visible = ref(false);
const currentStepIndex = ref(-1);
const progressPercent = ref(0);
const stepsContainerRef = ref<HTMLElement | null>(null);
const stepRefs = ref<any[]>([]);
const hasCompleted = ref(false); // 标记动画是否已完成
const apiCompletedLocal = ref(false); // 标记 API（customerLeaderAdopt）是否已完成
const detailDataLoadedLocal = ref(false); // 标记详情页数据是否已加载完成

const runAIThinking = async () => {
  hasCompleted.value = false;
  apiCompletedLocal.value = false;
  detailDataLoadedLocal.value = false;
  const totalSteps = props.steps.length;
  const startTime = Date.now();
  const minDuration = totalSteps * props.stepDelay; // 最小执行时间（12 步 × 800ms = 9.6 秒）

  console.log('开始动画', {
    totalSteps,
    stepDelay: props.stepDelay,
    minDuration,
    startStepIndex: props.startStepIndex,
    initialApiCompleted: props.apiCompleted
  });

  // 如果 startStepIndex > 0，直接显示前几步为已完成状态
  if (props.startStepIndex > 0) {
    currentStepIndex.value = props.startStepIndex - 1;
    progressPercent.value = Math.round((props.startStepIndex / totalSteps) * 100);
    console.log(`从第 ${props.startStepIndex + 1} 步开始，前 ${props.startStepIndex} 步已标记为完成`);
  }

  for (let i = props.startStepIndex; i < totalSteps; i++) {
    currentStepIndex.value = i;

    // 第 9 步（索引 8）的特殊处理：需要等待 API 完成才能继续
    if (i === 8 && !props.apiCompleted) {
      console.log('执行到第 9 步，API 未完成，等待 API 完成...');

      // 使用 watch 监听 apiCompleted 变化，避免轮询
      await new Promise(resolve => {
        const unwatch = watch(() => props.apiCompleted, (newValue) => {
          if (newValue) {
            console.log('API 已完成，继续执行');
            unwatch(); // 停止监听
            resolve(true);
          }
        }, { immediate: false });
      });
    }

    // 根据状态控制进度条显示
    const shouldWaitForData = !props.detailDataLoaded && i >= 11;
    const isLastStep = i === totalSteps - 1;
    console.log(`步骤 ${i + 1}, detailDataLoaded: ${props.detailDataLoaded}, shouldWaitForData: ${shouldWaitForData}`);

    if ((!props.apiCompleted && i >= 8) || shouldWaitForData || isLastStep) {
      // API 未完成时（第 9 步及以后）或详情页数据未完成时（最后一步）或正在执行最后一步时，进度控制在 99% 以内
      progressPercent.value = Math.min(99, Math.round(((i + 1) / totalSteps) * 99));
      console.log(`进度控制在 99%: ${progressPercent.value}%`);
    } else {
      // 正常显示进度
      progressPercent.value = Math.round(((i + 1) / totalSteps) * 100);
      console.log(`正常进度: ${progressPercent.value}%`);
    }

    // 滚动到当前步骤
    await nextTick();
    scrollToCurrentStep(i);

    // 如果是最后一步且详情页数据未加载完成，强制进度为 99%
    if (i === totalSteps - 1 && !props.detailDataLoaded) {
      progressPercent.value = 99;
      console.log('最后一步，详情页数据未加载，进度设置为 99%');
    }

    await new Promise(resolve => setTimeout(resolve, props.stepDelay));

    // 第 9 步（索引 8）执行完成后，触发跳转但不关闭
    if (i === 8) {
      console.log('第 9 步执行完成，触发跳转');
      emit('step9Complete');
    }
  }

  // 所有步骤执行完成后，检查是否已经过了最小时间
  const elapsed = Date.now() - startTime;
  const remainingTime = Math.max(0, minDuration - elapsed);

  console.log('动画步骤执行完成', {
    elapsed,
    remainingTime,
    minDuration,
    apiCompleted: props.apiCompleted,
    detailDataLoaded: props.detailDataLoaded
  });

  // 如果详情页数据已加载完成，不需要等待最小时间，直接关闭
  if (props.detailDataLoaded) {
    console.log('详情页数据已加载，跳过等待最小时间');
  } else if (remainingTime > 0) {
    // 否则等待剩余时间
    console.log(`等待剩余时间 ${remainingTime}ms`);
    await new Promise(resolve => setTimeout(resolve, remainingTime));
  }

  // 等待详情页数据加载完成
  if (!props.detailDataLoaded) {
    console.log('等待详情页数据加载完成...');
    // 确保进度条保持在 99%
    progressPercent.value = 99;
    await new Promise(resolve => {
      const checkData = setInterval(() => {
        if (props.detailDataLoaded) {
          clearInterval(checkData);
          resolve(true);
        }
      }, 100);
    });
    console.log('详情页数据已加载完成');
    // 数据加载完成后，进度跳到 100%
    progressPercent.value = 100;
  } else {
    console.log('详情页数据已经完成，无需等待');
  }

  // 延迟关闭
  await new Promise(resolve => setTimeout(resolve, 500));
  visible.value = false;
  emit('complete');
};

// 滚动到当前步骤
const scrollToCurrentStep = (index: number) => {
  const container = stepsContainerRef.value;
  const stepElement = stepRefs.value[index];

  if (container && stepElement) {
    const containerRect = container.getBoundingClientRect();
    const stepRect = stepElement.getBoundingClientRect();

    // 计算需要滚动的距离
    const scrollTop = container.scrollTop + stepRect.top - containerRect.top - 80;

    // 平滑滚动
    container.scrollTo({
      top: scrollTop,
      behavior: 'smooth'
    });
  }
};

onMounted(() => {
  // 不自动执行
});

// 监听 loadStatus 变化
watch(() => props.loadStatus, (newVal) => {
  // console.log('loadStatus changed:', newVal)

  if (newVal === true && !visible.value && !hasCompleted.value) {
    // 开始显示动画
    visible.value = true
    runAIThinking()
  }
}, { immediate: true });

defineExpose({})
</script>

<style scoped lang="less">
.ai-thinking-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.75) 0%, rgba(118, 75, 162, 0.7) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px);
    background-size: 50px 50px;
    animation: rotate 20s linear infinite;
  }

  .ai-thinking-content {
    position: relative;
    z-index: 1;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 24px;
    padding: 48px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    max-width: 600px;
    width: 90%;
    text-align: center;
  }

  .ai-brain {
    position: relative;
    width: 120px;
    height: 120px;
    margin: 0 auto 32px;

    .brain-ring {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: 3px solid transparent;
      border-top-color: #667eea;
      border-radius: 50%;
      animation: spin 1.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;

      &:nth-child(2) {
        width: 80%;
        height: 80%;
        top: 10%;
        left: 10%;
        border-top-color: #764ba2;
        animation-delay: -0.5s;
      }

      &:nth-child(3) {
        width: 60%;
        height: 60%;
        top: 20%;
        left: 20%;
        border-top-color: #f093fb;
        animation-delay: -1s;
      }
    }

    .brain-core {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 60px;
      height: 60px;
      background: linear-gradient(135deg, #667eea, #764ba2);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 20px rgba(102, 126, 234, 0.5);
      animation: pulse 2s ease-in-out infinite;

      .brain-icon {
        width: 36px;
        height: 36px;
        color: #fff;

        svg {
          width: 100%;
          height: 100%;
        }
      }
    }
  }

  .ai-thinking-text {
    .thinking-title {
      font-size: 24px;
      font-weight: bold;
      color: #333;
      margin-bottom: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;

      .title-icon {
        font-size: 20px;
        animation: sparkle 1.5s ease-in-out infinite;

        &:nth-child(3) {
          animation-delay: 0.3s;
        }
      }
    }

    .thinking-steps {
      text-align: left;
      margin-bottom: 24px;
      max-height: 360px;
      overflow-y: auto;
      padding-right: 8px;
      scroll-behavior: smooth;

      &::-webkit-scrollbar {
        width: 6px;
      }

      &::-webkit-scrollbar-thumb {
        background: #e0e0e0;
        border-radius: 3px;
      }

      .thinking-step {
        display: flex;
        align-items: center;
        padding: 10px 12px;
        margin: 6px 0;
        border-radius: 8px;
        transition: all 0.3s ease;
        opacity: 0.5;
        transform: translateX(-10px);

        &.active {
          opacity: 1;
          background: linear-gradient(90deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1));
          transform: translateX(0);

          .step-text {
            color: #667eea;
            font-weight: 500;
          }
        }

        &.completed {
          opacity: 0.8;
          background: rgba(82, 196, 26, 0.1);

          .step-text {
            color: #52c41a;
          }

          .step-indicator {
            background: #52c41a;
            border-color: #52c41a;
          }
        }

        .step-indicator {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: #e0e0e0;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 12px;
          flex-shrink: 0;
          transition: all 0.3s ease;

          .step-check {
            color: #fff;
            font-size: 14px;
            font-weight: bold;
          }

          .step-loading {
            width: 12px;
            height: 12px;
            border: 2px solid #667eea;
            border-top-color: transparent;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }

          .step-dot {
            color: #999;
            font-size: 18px;
          }
        }

        .step-text {
          font-size: 14px;
          color: #666;
          transition: all 0.3s ease;
        }
      }
    }

    .ai-progress {
      display: flex;
      align-items: center;
      gap: 12px;

      .progress-bar {
        flex: 1;
        height: 8px;
        background: #e0e0e0;
        border-radius: 4px;
        overflow: hidden;

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #667eea, #764ba2);
          border-radius: 4px;
          transition: width 0.3s ease;
        }
      }

      .progress-text {
        font-size: 16px;
        font-weight: bold;
        color: #667eea;
        min-width: 45px;
        text-align: right;
      }
    }
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes pulse {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
    box-shadow: 0 4px 20px rgba(102, 126, 234, 0.5);
  }
  50% {
    transform: translate(-50%, -50%) scale(1.05);
    box-shadow: 0 6px 30px rgba(102, 126, 234, 0.7);
  }
}

@keyframes rotate {
  to {
    transform: rotate(360deg);
  }
}

@keyframes sparkle {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.2);
  }
}

.step-fade-enter-active,
.step-fade-leave-active {
  transition: all 0.3s ease;
}

.step-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.step-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
